/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: roles entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the roles table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the roles API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, organization_id, name, description, is_default, created_at, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, organization_id]
 */

import { httpClient } from "../http-client.js";
import type { UnifiedQueryOptions } from "../../../../../config/search/global-search-config.js";
import type {
  RolesRow,
  RolesInsert,
  RolesUpdate,
} from "../../types/generated_schemas/generated-roles-schema.js";

export interface RolesQueryOptions extends UnifiedQueryOptions {}

export interface RolesResponse {
  data: RolesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for roles table operations
 *
 * @description Provides type-safe access to roles API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class RolesClient {
  private readonly basePath = "/roles";

  /**
   * Get all roles records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of roles records
   */
  async getAll(options?: RolesQueryOptions): Promise<RolesRow[]> {
    const params = new URLSearchParams();

    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());
    if (options?.sortBy) params.set("sortBy", options.sortBy);
    if (options?.sortOrder) params.set("sortOrder", options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await httpClient.get<RolesResponse>(url);
    return response.data;
  }

  /**
   * Get a single roles record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the roles record
   */
  async getById(id: string): Promise<RolesRow> {
    const response = await httpClient.get<{
      data: RolesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new roles record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created roles record
   */
  async create(data: RolesInsert): Promise<RolesRow> {
    const response = await httpClient.post<{
      data: RolesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing roles record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated roles record
   */
  async update(id: string, data: RolesUpdate): Promise<RolesRow> {
    const response = await httpClient.put<{
      data: RolesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a roles record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple roles records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created roles records
   */
  async createMany(data: RolesInsert[]): Promise<RolesRow[]> {
    const response = await httpClient.post<{
      data: RolesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple roles records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search roles with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: RolesQueryOptions,
  ): Promise<RolesResponse> {
    throw new Error(
      "Search operations are not available for roles - no searchable fields configured",
    );
  }

  /**
   * Get count of roles records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString
      ? `${this.basePath}/count?${queryString}`
      : `${this.basePath}/count`;

    const response = await httpClient.get<{ data: number; success: boolean }>(
      url,
    );
    return response.data;
  }

  /**
   * Check if a roles record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: "roles",
      publicFields: [
        "id",
        "organization_id",
        "name",
        "description",
        "is_default",
        "created_at",
        "updated_at",
      ],
      searchableFields: [],
      filterableFields: ["id", "organization_id"],
      sortableFields: ["created_at", "updated_at"],
      readonlyFields: ["id", "created_at", "updated_at"],
      requiredFields: ["id", "name"],
    };
  }
}

// Export singleton instance for convenience
export const rolesClient = new RolesClient();
